import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe2, Camera, Stamp } from "lucide-react";
import PageHero from "../components/PageHero";
import CTASection from "../components/CTASection";
import { Button } from "../components/ui/button";
import { INTERNATIONAL_DOCUMENTS, PASSPORT_SERVICES, PHOTO_SERVICES, NOTARY_SERVICES, IMAGES } from "../data/content";

const ROUTER_OPTIONS = [
  {
    id: "us",
    label: "United States",
    text: "Documents used inside the U.S. usually need notarization, certified translation (if not in English), or an apostille if they will later go abroad. We help you prepare the right package.",
    cta: "Request Document Help",
  },
  {
    id: "abroad",
    label: "Another country",
    text: "Documents leaving the U.S. may need an apostille (Hague Convention countries) or authentication plus embassy legalization (non-Hague countries). We identify the correct path for your destination country.",
    cta: "Start an Apostille Request",
  },
  {
    id: "unsure",
    label: "Not sure",
    text: "No problem. Tell us what the document is and why you need it — we will research the requirements and explain your options in plain language before anything is filed.",
    cta: "Ask a Document Specialist",
  },
];

export default function DocumentsIdentity() {
  const [route, setRoute] = useState(null);
  const selected = ROUTER_OPTIONS.find((o) => o.id === route);

  return (
    <div data-testid="documents-page">
      <PageHero
        eyebrow="Documents & Identity"
        title="Documents & Identity Services"
        lead="Immigration and life in a new country often depend on documents located somewhere else. CF Full Circle helps clients understand what records are needed, where they come from, how to request them, and how to get them ready for use."
        image={IMAGES.passport}
        alt="Passport and identity documents"
      />

      {/* INTERNATIONAL DOCUMENT RETRIEVAL */}
      <section className="py-16 lg:py-24" data-testid="intl-docs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="h-11 w-11 rounded-xl bg-teal-light flex items-center justify-center mb-5">
                <Globe2 className="h-5 w-5 text-teal" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-navy tracking-tight">International Document Retrieval</h2>
              <p className="mt-4 text-base text-mutedink leading-relaxed">
                If you are living in the United States and need records from your country of origin or another country, we can help research the process, prepare requests, coordinate authorized retrieval, track the request, and arrange authentication, translation, or delivery where permitted.
              </p>
              <p className="mt-4 text-sm text-mutedink italic">
                Government agencies and record custodians control document issuance, eligibility, timing, and fees.
              </p>
              <Button asChild className="mt-6 bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full" data-testid="request-document-button">
                <Link to="/start">Request a Document <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {INTERNATIONAL_DOCUMENTS.map((d) => (
                <div key={d} className="flex items-center gap-3 bg-surface border border-line rounded-lg px-4 py-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-teal flex-shrink-0" />
                  <span className="text-sm text-ink">{d}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PASSPORT & IDENTITY */}
      <section className="py-16 lg:py-24 bg-sand/60" data-testid="passport-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-navy tracking-tight mb-10">Passport &amp; Identity Assistance</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {PASSPORT_SERVICES.map((s) => (
              <div key={s} className="flex items-start gap-3 bg-surface border border-line rounded-lg p-4">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-sage flex-shrink-0" />
                <span className="text-sm text-ink">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTOGRAPHY */}
      <section className="py-16 lg:py-20" data-testid="photo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <img src={IMAGES.workspace} alt="Professional photo and document workspace" className="rounded-2xl w-full h-72 object-cover shadow-md" />
            <div>
              <div className="h-11 w-11 rounded-xl bg-champagne-light flex items-center justify-center mb-5">
                <Camera className="h-5 w-5 text-champagne-dark" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-navy tracking-tight">Passport &amp; Visa Photography</h2>
              <div className="mt-6 flex flex-wrap gap-2">
                {PHOTO_SERVICES.map((s) => (
                  <span key={s} className="bg-teal-light text-teal text-xs font-medium px-3 py-1.5 rounded-full">{s}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NOTARY & APOSTILLE */}
      <section className="py-16 lg:py-24 bg-sand/60" data-testid="notary-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <div className="h-11 w-11 rounded-xl bg-sage-light flex items-center justify-center mb-5">
              <Stamp className="h-5 w-5 text-sage" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-navy tracking-tight">Notary, Apostille &amp; Authentication</h2>
            <p className="mt-4 text-base text-mutedink">Prepare documents for use in the United States or internationally.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {NOTARY_SERVICES.map((s) => (
              <div key={s.title} className="bg-surface border border-line rounded-xl p-6 shadow-sm">
                <h3 className="text-base font-sans font-semibold text-navy">{s.title}</h3>
                <p className="mt-2 text-sm text-mutedink leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>

          {/* INTERACTIVE ROUTER */}
          <div className="mt-16 bg-surface border border-line rounded-2xl p-8 shadow-sm" data-testid="doc-router">
            <h3 className="text-xl font-serif font-semibold text-navy">Where will your document be used?</h3>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              {ROUTER_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  onClick={() => setRoute(o.id)}
                  data-testid={`doc-router-${o.id}`}
                  className={`flex-1 rounded-xl border px-5 py-4 text-sm font-medium transition-all ${route === o.id ? "border-teal bg-teal-light text-teal" : "border-line bg-ivory text-navy hover:border-teal"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {selected && (
              <div className="mt-6 border-t border-line pt-6 animate-fade-up" data-testid="doc-router-result">
                <p className="text-sm text-mutedink leading-relaxed max-w-2xl">{selected.text}</p>
                <Button asChild className="mt-5 bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full" data-testid="doc-router-cta">
                  <Link to="/start">{selected.cta} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      <CTASection title="Need a document from another country?" text="Tell us what you need and where it lives. We research the process and coordinate the steps." primaryLabel="Get Document Assistance" />
    </div>
  );
}
